import React, { useEffect, useRef, useState } from "react";
import { MoveLeft } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button, Spinner, ToastContainer, Toast } from "react-bootstrap";
import DOMPurify from "dompurify";
import Sidebar from "../common/Sidebar";
import CommonDropdown from "../common/common_dropDown";
import DateRangePicker from "../common/dateRange"; // optional if needed elsewhere
import ErrorExtractor from "../common/error_extractor";
import { ReactComponent as UploadIc } from "../../assets/svg/upload.svg";
import { submitProductAPI, updateProductAPI, getProductEditApi, addProductAttachmentApi } from "./product-api";
import SearchWithFreeText from "../common/search-free-text";
import { Navbar } from "../common/Navbar";

const emptyDimension = {
  length: "",
  breadth: "",
  height: "",
  quantity: "",
  price: "",
};

export const ProductCreate = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const adminUrl = process.env.REACT_APP_ADMIN_API_URL;

  const location = useLocation();
  const navigate = useNavigate();
  const { id } = location.state || {};

  useEffect(() => {
    if (id != null) getProductEdit(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const [selectedProductType, setSelectedProductType] = useState(null);
  const [searchCreiteria, setSearchCriteria] = useState(null);
  const [selectedCreiteria, setSelectedCreiteria] = useState(null);
  const [searchProduct, setSearchProduct] = useState("");
  const [isLoader, setIsLoader] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarStatus, setSnackbarStatus] = useState(false);
  const [productId, setProductId] = useState("");
  const [productName, setProductName] = useState("");
  const [weightOption, setWeightOption] = useState("");
  const [typeOption, setTypeOption] = useState(null);
  const [criteriaOption, setCriteriaOption] = useState(null);
  const [statusOption, setStatusOption] = useState("true");
  const [dimensionUnit, setDimensionUnit] = useState("");
  const [boxdimensionUnit, setBoxDimensionUnit] = useState("");
  const [customdimensionUnit, setCustomDimensionUnit] = useState("");
  const [defaultValue, setDefaultValue] = useState(false);

  const [singleDimension, setSingleDimension] = useState({ ...emptyDimension });
  const [boxDimension, setBoxDimension] = useState({ ...emptyDimension });
  const [customDimension, setCustomDimension] = useState({ ...emptyDimension });

  const [description, setDescription] = useState("");
  const [images, setImages] = useState([]);
  const [errorMsg, setErrorMsg] = useState({});
  const [files, setFiles] = useState([]);
  const [removedAttachmentIds, setRemovedAttachmentIds] = useState([]);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);

    const mapped = selectedFiles.map((file) => ({
      file,
      preview: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
    }));

    setFiles((prev) => [...prev, ...mapped]); // append to existing list
  };
  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files);

    const mapped = droppedFiles.map((file) => ({
      file,
      preview: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
    }));

    setFiles((prev) => [...prev, ...mapped]);
  };

  const removeImage = (index) => {
    const target = files[index];
    if (target.id) {
      setRemovedAttachmentIds((prev) => [...prev, target.id]);
    }
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };
  const handleDropdownProductchange = (option) => {
    console.log("Selected option:", option);
    setSelectedProductType(option);
    setSearchProduct(option.value ?? "");
  };

  const handleSearchProductChange = (value) => {
    console.log("Search input changed:", value);
    setSearchProduct(value);
    setSelectedProductType({ label: value, value: value });
  };
  const handleDropdownCriteriachange = (option) => {
    console.log("Selected option:", option);
    setSelectedCreiteria(option);
    setSearchCriteria(option.value ?? "");
  };

  const handleSearchCriteriaChange = (value) => {
    console.log("Search input changed:", value);
    setSearchCriteria(value);
    setSelectedCreiteria({ label: value, value: value });
  };

  const getProductEdit = async (pid) => {
    try {
      const response = await getProductEditApi(pid);
      if (response?.data?.success) {
        const p = response.data.products ?? {};
        setProductId(p.item_code ?? "");
        setProductName(p.item_name ?? "");
        setWeightOption(p.weight ?? "");
        setTypeOption(p.type ? { value: p.type, label: p.type } : null);
        setSelectedProductType({ value: p.type, label: p.type });
        setSearchProduct(p.type ?? "");
        setSelectedCreiteria({ value: p.criteria, label: p.criteria });
        setSearchCriteria(p.criteria ?? "");
        setStatusOption(String(p.status));
        const single = p.uoms?.find((u) => u.uom_name === "Single") || {};
        const box = p.uoms?.find((u) => u.uom_name === "Box") || {};
        const custom = p.uoms?.find((u) => u.uom_name === "Custom") || {};
        setDimensionUnit(single.measurement_type ?? "");
        setBoxDimensionUnit(box.measurement_type ?? "");
        setCustomDimensionUnit(custom.measurement_type ?? "");
        setDefaultValue(p.default ?? false);

        setSingleDimension({
          length: single.length ?? "",
          breadth: single.width ?? "",
          height: single.height ?? "",
          quantity: single.quantity ?? "",
          price: single.price ?? "",
        });

        setBoxDimension({
          length: box.length ?? "",
          breadth: box.width ?? "",
          height: box.height ?? "",
          quantity: box.quantity ?? "",
          price: box.price ?? "",
        });

        setCustomDimension({
          length: custom.length ?? "",
          breadth: custom.width ?? "",
          height: custom.height ?? "",
          quantity: custom.quantity ?? "",
          price: custom.price ?? "",
        });

        setDescription(p.details ?? "");
        setFiles(
          (p.product_attachments ?? []).map((att) => ({
            file: null,
            preview: att.file_path,
            name: att.file_path?.split("/").pop().split("?")[0],
            id: att.id,
          }))
        );
      }
    } catch (err) {
      console.error("Error fetching product:", err);
    }
  };

  const sanitizePayload = (payload) => {
    const sanitized = {};
    Object.entries(payload).forEach(([k, v]) => {
      if (typeof v === "boolean" || typeof v === "number" || Array.isArray(v)) {
        sanitized[k] = v;
      } else {
        sanitized[k] = DOMPurify.sanitize(v ?? "");
      }
    });
    return sanitized;
  };
  const buildUOM = (name, dimension) => ({
    uom_name: name,
    quantity: Number(dimension.quantity) || 0,
    price: Number(dimension.price) || 0,
    measurement_type: name === "Single" ? dimensionUnit : name === "Box" ? boxdimensionUnit : customdimensionUnit, // inch, cm, ft, meter
    length: Number(dimension.length) || 0,
    width: Number(dimension.breadth) || 0,
    height: Number(dimension.height) || 0,
  });
  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });

  const submitForm = async () => {
    setIsLoader(true);

    try {
      const payload = {
        item_code: productId ?? "",
        item_name: productName ?? "",
        weight: String(weightOption || "").trim() || "0",

        type: selectedProductType?.value ?? "",
        criteria: selectedCreiteria?.value ?? "",
        status: statusOption != null ? statusOption === "true" : true,
        uoms: [buildUOM("Single", singleDimension), buildUOM("Box", boxDimension), buildUOM("Custom", customDimension)],
        details: description,
        default: defaultValue,
        remove_attachments: removedAttachmentIds,
      };

      const sanitized = sanitizePayload(payload);

      let response = null;

      if (id == null) {
        response = await submitProductAPI(sanitized);
      } else {
        response = await updateProductAPI(id, sanitized);
      }

      if (response?.data?.success === true) {
        const newProductId = response.data.product_id || id;

        // Filter for new files only (those with a 'file' property)
        const newFiles = files.filter((item) => item.file);

        setSnackbarMessage(response.data.message || "Saved");
        setSnackbarStatus(true);
        setShowSnackbar(true);

        setIsLoader(false);
        if (newFiles.length > 0) {
          const attachmentsBase64 = await Promise.all(newFiles.map(async (item) => await fileToBase64(item.file)));
          await addProductAttachmentApi(newProductId, {
            attachments: attachmentsBase64,
          });
        }
        navigate(-1);
      } else if (response?.status === 400 || response?.data?.error) {
        console.log("Changedsfds", response.data?.error);

        const backendError = response.data?.error;

        if (backendError) {
          const formattedErrors = Object.fromEntries(Object.entries(backendError).map(([key, value]) => [key, value[0]]));

          setErrorMsg(formattedErrors);
        }
      } else {
        setSnackbarMessage("Unexpected error");
        setSnackbarStatus(false);
        setShowSnackbar(true);
      }
    } catch (err) {
      console.error("submit product error", err);
      console.error("submit product error", err);

      const apiError = err?.response?.data;
      console.log("Changedsfds catch", apiError);
      if (apiError) {
        const extractor = new ErrorExtractor();

        // Priority: backend error → fallback to message → default msg
        const parsedErrors = extractor.extractErrors(apiError.error || apiError.errors || apiError.message || {}, {});

        setErrorMsg(parsedErrors);
      }

      setSnackbarMessage(apiError.message);
      setSnackbarStatus(false);
      setShowSnackbar(true);
    } finally {
      setIsLoader(false);
    }
  };

  return (
    <div className="d-flex">
      <Sidebar currentfile="Product" />
      <div className="rounded-4 flex-grow-1">
        <div style={{ background: "#F5F8FA" }}>
          <Navbar pageTitle="Product" />
          <div className="px-2 d-flex align-items-center justify-content-between">
            <div className="d-flex gap-3 my-3">
              <MoveLeft className="cursor-pointer" onClick={() => navigate(-1)} />
              <p className="fw-semibold m-0">{id ? "Edit Product" : "New Product"}</p>
            </div>

            <div className="d-flex gap-3">
              <Button className="cursor-pointer bg-white rounded-pill px-4 py-2 btn-border-grey fs-15 fw-500" style={{ border: "1px solid #D4DDE7", color: "#212129" }} onClick={() => navigate(-1)}>
                Cancel
              </Button>

              <Button className={`cursor-pointer dark-purple-btn ${isLoader ? "disabled-btn" : ""} rounded-pill py-2 px-4 d-flex justify-content-center align-items-center`} onClick={submitForm} style={{ minWidth: "100px" }}>
                {isLoader ? <Spinner animation="border" size="sm" /> : id ? "Update" : "Save"}
              </Button>
            </div>
          </div>

          <div className="p-3">
            <div className="row">
              <div className="col-12 col-lg-8">
                <div className="bg-white rounded-4 p-3 mb-3">
                  <div className="row">
                    <div className="col-6 text-start">
                      <label className="form-label fs-12 fw-400">
                        Product ID <span style={{ color: "red" }}>*</span>
                      </label>

                      <input type="text" className="form-control rounded-3" value={productId} placeholder="Enter here" style={{ height: "36px" }} onChange={(e) => setProductId(e.target.value)} />

                      <div className="text-danger fs-12">{errorMsg.item_code}</div>
                    </div>
                    <div className="col-6 text-start">
                      <label className="form-label fs-12 fw-400">
                        Product Name<span style={{ color: "red" }}>*</span>
                      </label>
                      <input type="text" placeholder="Enter here" className="form-control rounded-3" style={{ height: "36px" }} value={productName} onChange={(e) => setProductName(e.target.value)} />
                      <div className="text-danger fs-12">{errorMsg.item_name}</div>
                    </div>
                    <div className="col-6 mt-3 text-start">
                      <label className="form-label fs-12 fw-400">Weight</label>
                      <input type="number" className="form-control rounded-3" value={weightOption} style={{ height: "36px" }} placeholder="Enter here" onChange={(e) => setWeightOption(e.target.value)} />
                      <div className="text-danger fs-12">{errorMsg.weight}</div>
                    </div>
                    <div className="col-6 mt-3 text-start">
                      <label className="form-label fs-12 fw-400">
                        Type<span style={{ color: "red" }}>*</span>
                      </label>
                      <SearchWithFreeText apiUrl={`${apiUrl}get_product_types`} placeholder="Search & type..." selectedValue={selectedProductType} onDropdownChange={handleDropdownProductchange} onChangeSearch={handleSearchProductChange} /> <div className="text-danger fs-12">{errorMsg.type}</div>
                    </div>{" "}
                    <div className="col-6 mt-3 text-start">
                      <label className="form-label fs-12 fw-400">Criteria</label>
                      <SearchWithFreeText apiUrl={`${apiUrl}products/criteria`} placeholder="Search & type..." selectedValue={selectedCreiteria} onDropdownChange={handleDropdownCriteriachange} onChangeSearch={handleSearchCriteriaChange} /> <div className="text-danger fs-12">{errorMsg.criteria}</div>
                    </div>
                    <div className="col-6 mt-3 text-start">
                      <label className="form-label fs-12 fw-400">Status</label>

                      <select className="form-select rounded-3" value={statusOption} onChange={(e) => setStatusOption(e.target.value)}>
                        <option value="true">Active</option>
                        <option value="false">InActive</option>
                      </select>

                      <div className="text-danger fs-12">{errorMsg?.status}</div>
                    </div>
                    <div className="col-6 mt-3 text-start d-flex align-items-baseline gap-2">
                      <input type="checkbox" checked={defaultValue} onChange={(e) => setDefaultValue(e.target.checked)} />
                      <label className="form-label fs-12 fw-400">Default</label>
                      <div className="text-danger fs-12 fw-400">{errorMsg?.default}</div>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-4 p-3 mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="fs-14 fw-700">Single Product Dimension</span>

                    <select className="form-select" style={{ width: "100px", height: "32px" }} value={dimensionUnit} onChange={(e) => setDimensionUnit(e.target.value)}>
                      <option value="ft">ft</option>
                      <option value="inch">inch</option>
                      <option value="cm">cm</option>
                    </select>
                  </div>

                  <div className="d-flex flex-wrap w-100 gap-2">
                    {["length", "breadth", "height"].map((k) => (
                      <div key={k} style={{ flex: 1, minWidth: "120px" }} className="text-start">
                        <label className="form-label fs-12 fw-400 text-muted">{k.charAt(0).toUpperCase() + k.slice(1)}</label>
                        <input
                          type="number"
                          placeholder="Enter here"
                          className="form-control rounded-3"
                          value={singleDimension[k]}
                          onChange={(e) =>
                            setSingleDimension({
                              ...singleDimension,
                              [k]: e.target.value,
                            })
                          }
                          style={{ height: "36px" }}
                        />
                      </div>
                    ))}

                    <div style={{ flex: 1, minWidth: "120px" }} className="text-start">
                      <label className="form-label fs-12 fw-400">Qty</label>
                      <input
                        type="number"
                        placeholder="Enter here"
                        className="form-control rounded-3"
                        value={singleDimension.quantity}
                        onChange={(e) =>
                          setSingleDimension({
                            ...singleDimension,
                            quantity: e.target.value,
                          })
                        }
                        style={{ height: "36px" }}
                      />
                    </div>

                    <div style={{ flex: 1, minWidth: "120px" }} className="text-start">
                      <label className="form-label fs-12 fw-400">Price</label>
                      <input
                        type="number"
                        placeholder="Enter here"
                        className="form-control rounded-3"
                        value={singleDimension.price}
                        onChange={(e) =>
                          setSingleDimension({
                            ...singleDimension,
                            price: e.target.value,
                          })
                        }
                        style={{ height: "36px" }}
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-4 p-3 mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="fs-14 fw-700">Box Product Dimension</span>

                    <select className="form-select" style={{ width: "100px", height: "32px" }} value={boxdimensionUnit} onChange={(e) => setBoxDimensionUnit(e.target.value)}>
                      <option value="ft">ft</option>
                      <option value="inch">inch</option>
                      <option value="cm">cm</option>
                    </select>
                  </div>

                  <div className="d-flex flex-wrap w-100 gap-2">
                    {["length", "breadth", "height"].map((k) => (
                      <div key={k} style={{ flex: 1, minWidth: "120px" }} className="text-start">
                        <label className="form-label fs-12 fw-400 text-muted">{k.charAt(0).toUpperCase() + k.slice(1)}</label>
                        <input
                          type="number"
                          placeholder="Enter here"
                          className="form-control rounded-3"
                          value={boxDimension[k]}
                          onChange={(e) =>
                            setBoxDimension({
                              ...boxDimension,
                              [k]: e.target.value,
                            })
                          }
                          style={{ height: "36px" }}
                        />
                      </div>
                    ))}

                    <div style={{ flex: 1, minWidth: "120px" }} className="text-start">
                      <label className="form-label fs-12 fw-400">Qty</label>
                      <input
                        type="number"
                        placeholder="Enter here"
                        className="form-control rounded-3"
                        value={boxDimension.quantity}
                        onChange={(e) =>
                          setBoxDimension({
                            ...boxDimension,
                            quantity: e.target.value,
                          })
                        }
                        style={{ height: "36px" }}
                      />
                    </div>

                    <div style={{ flex: 1, minWidth: "120px" }} className="text-start">
                      <label className="form-label fs-12 fw-400">Price</label>
                      <input
                        type="number"
                        placeholder="Enter here"
                        className="form-control rounded-3"
                        value={boxDimension.price}
                        onChange={(e) =>
                          setBoxDimension({
                            ...boxDimension,
                            price: e.target.value,
                          })
                        }
                        style={{ height: "36px" }}
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-4 p-3 mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="fs-14 fw-700">Custom Product Dimension</span>

                    <select className="form-select" style={{ width: "100px", height: "32px" }} value={customdimensionUnit} onChange={(e) => setCustomDimensionUnit(e.target.value)}>
                      <option value="ft">ft</option>
                      <option value="inch">inch</option>
                      <option value="cm">cm</option>
                    </select>
                  </div>

                  <div className="d-flex flex-wrap w-100 gap-2">
                    {["length", "breadth", "height"].map((k) => (
                      <div key={k} style={{ flex: 1, minWidth: "120px" }} className="text-start">
                        <label className="form-label fs-12 fw-400 text-muted">{k.charAt(0).toUpperCase() + k.slice(1)}</label>
                        <input
                          type="number"
                          placeholder="Enter here"
                          className="form-control rounded-3"
                          value={customDimension[k]}
                          onChange={(e) =>
                            setCustomDimension({
                              ...customDimension,
                              [k]: e.target.value,
                            })
                          }
                          style={{ height: "36px" }}
                        />
                      </div>
                    ))}

                    <div style={{ flex: 1, minWidth: "120px" }} className="text-start">
                      <label className="form-label fs-12 fw-400">Qty</label>
                      <input
                        type="number"
                        placeholder="Enter here"
                        className="form-control rounded-3"
                        value={customDimension.quantity}
                        onChange={(e) =>
                          setCustomDimension({
                            ...customDimension,
                            quantity: e.target.value,
                          })
                        }
                        style={{ height: "36px" }}
                      />
                    </div>

                    <div style={{ flex: 1, minWidth: "120px" }} className="text-start">
                      <label className="form-label fs-12 fw-400">Price</label>
                      <input
                        type="number"
                        placeholder="Enter here"
                        className="form-control rounded-3"
                        value={customDimension.price}
                        onChange={(e) =>
                          setCustomDimension({
                            ...customDimension,
                            price: e.target.value,
                          })
                        }
                        style={{ height: "36px" }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-12 col-lg-4">
                <div className="bg-white rounded-4 p-3 mb-3 text-start">
                  <div className="fs-14 fw-700 mb-2">Product Images</div>
                  <div
                    className="border rounded d-flex flex-column align-items-center justify-content-center p-4 text-center"
                    style={{
                      backgroundColor: "#f8f9fa",
                      borderColor: "#dee2e6",
                      minHeight: "140px",
                      borderStyle: "dashed",
                      cursor: "pointer",
                    }}
                    onClick={() => fileInputRef.current.click()}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={handleDrop}
                  >
                    <div className="mb-2 text-primary">
                      <UploadIc width="32" height="32" />
                    </div>
                    <div className="fs-12 fw-500 mb-1 text-primary">Click or Drop images here</div>
                    <div className="text-muted" style={{ fontSize: "10px" }}>
                      Upload multiple images
                    </div>

                    <input ref={fileInputRef} type="file" multiple onChange={handleFileSelect} style={{ display: "none" }} />
                  </div>
                  {files.length > 0 && (
                    <div className="d-flex gap-2 flex-wrap mt-3 w-100">
                      {files.map((item, index) => (
                        <div key={index} className="position-relative" style={{ width: 90, height: 90 }}>
                          {item.preview ? (
                            <img
                              src={item.preview}
                              alt="preview"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                borderRadius: "6px",
                              }}
                            />
                          ) : (
                            <div className="d-flex align-items-center justify-content-center w-100 h-100 bg-light rounded">{item.file?.name ?? item.name}</div>
                          )}

                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              removeImage(index);
                            }}
                            className="btn btn-danger btn-sm position-absolute"
                            style={{
                              top: "-6px",
                              right: "-6px",
                              padding: "2px 6px",
                              fontSize: "10px",
                              borderRadius: "50%",
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* <div
                    className="border rounded-3 p-3 text-center"
                    style={{ minHeight: "160px" }}
                  >
                    <div className="mb-2">Drag and drop or</div>
                    <input type="file" multiple onChange={handleFileChange} />
                    <div className="mt-2 text-muted fs-12">
                      {(images || []).length} file(s) selected
                    </div>
                  </div> */}
                </div>

                <div className="bg-white rounded-4 p-3 mb-3 text-start">
                  <div className="fs-14 fw-700 mb-2">Description</div>
                  <textarea className="form-control rounded-3" rows={4} value={description} onChange={(e) => setDescription(e.target.value)} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer position="top-end">
        <Toast bg={snackbarStatus ? "success" : "danger"} show={showSnackbar} onClose={() => setShowSnackbar(false)} delay={3000} autohide>
          <Toast.Body style={{ color: "#fff" }}>{snackbarMessage}</Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
};

export default ProductCreate;
